/** Base API client - centralized API client with interceptors. */

import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

import { getApiUrl } from "@/config/api";
import { authFetchCredentials } from "@/config/authStorage";
import { invalidateSession, refreshSessionTokens } from "@/lib/auth/permissions";
import { requestInterceptor, handleUnauthorizedResponse, SESSION_EXPIRED_ERROR_CODE } from "@/lib/api/interceptors";
import type { ApiResponse, PaginatedApiResponse, ListApiResponse } from "@/lib/api/types";
import { errorFromResponse, isRetryableError } from "@/lib/utils/response";
import type { ErrorResponse } from "@/lib/api/types";
import { apiService as gatewayApiService } from "@/services/api/apiService";
import { readLocaleSnapshot } from "@/lib/i18n/context";
import { getProjectHeaders } from "@/services/api/base";
import type { APIResponse as GatewayAPIResponse } from "@/types/api";
import { API_BASE_URL, HEADER_REQUEST_ID, HEADER_TRACE_ID, REQUEST_TIMEOUT } from "@/lib/constants/api";
import { v4 as uuidv4 } from "@/lib/uuid";

export interface ApiDownloadedFile {
  blob: Blob;
  filename: string | null;
}

/**
 * API client class - handles all API requests.
 */
class ApiClient {
  private client: AxiosInstance;
  private static readonly LEGACY_META = {
    request_id: "legacy-proxy",
    timestamp: "1970-01-01T00:00:00.000Z",
    version: "legacy",
  } as const;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: REQUEST_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    // Add request interceptor
    this.client.interceptors.request.use(requestInterceptor, (error) => Promise.reject(error));

    this.client.interceptors.response.use(
      (response) => response,
      (error) => handleUnauthorizedResponse(error, this.client)
    );
  }

  private normalizeApiResponse<T>(payload: unknown): ApiResponse<T> {
    if (payload && typeof payload === "object" && "success" in payload) {
      return payload as ApiResponse<T>;
    }
    return {
      success: true,
      data: payload as T,
      meta: ApiClient.LEGACY_META,
    };
  }

  private gatewayPath(url: string): string {
    return url.startsWith("/api/") ? url.slice(4) : url;
  }

  private resolveFileUrl(url: string): string {
    return getApiUrl(this.gatewayPath(url));
  }

  private sessionExpiredError(): ErrorResponse {
    return {
      success: false,
      error: {
        code: SESSION_EXPIRED_ERROR_CODE,
        message: "Session expired",
        details: { status: 401 },
      },
      meta: ApiClient.LEGACY_META,
    };
  }

  private buildFileRequestHeaders(body?: BodyInit | null): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: "application/octet-stream,*/*",
      "Accept-Language": readLocaleSnapshot(),
      ...getProjectHeaders(),
    };
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
    if (!isFormData && body !== null && body !== undefined && !(body instanceof Blob)) {
      headers["Content-Type"] = "application/json";
    }
    const requestId = uuidv4().replace(/-/g, "");
    headers[HEADER_REQUEST_ID] = requestId;
    headers[HEADER_TRACE_ID] = requestId;
    return headers;
  }

  private filenameFromDisposition(disposition: string | null): string | null {
    if (!disposition) return null;
    const match = disposition.match(/filename=(.+)/);
    if (!match) return null;
    const raw = match[1].replace(/^"|"$/g, "");
    if (raw.length === 0) return null;
    return raw;
  }

  private async fetchBinaryResponse(
    url: string,
    init: RequestInit,
    config?: AxiosRequestConfig,
    authRetried: boolean = false,
  ): Promise<Response> {
    const response = await fetch(this.resolveFileUrl(url), {
      ...init,
      credentials: authFetchCredentials(),
      headers: {
        ...this.buildFileRequestHeaders(init.body ?? null),
        ...(init.headers as Record<string, string> | undefined),
        ...(config?.headers as Record<string, string> | undefined),
      },
      signal: (config?.signal ?? init.signal) as AbortSignal | undefined,
    });
    if (response.status === 401) {
      if (!authRetried) {
        const refreshed = await refreshSessionTokens();
        if (refreshed) {
          return this.fetchBinaryResponse(url, init, config, true);
        }
      }
      invalidateSession();
      throw this.sessionExpiredError();
    }
    if (!response.ok) {
      throw {
        success: false,
        error: {
          code: "FILE_DOWNLOAD_FAILED",
          message: `File request failed (${response.status})`,
          details: { status: response.status },
        },
        meta: ApiClient.LEGACY_META,
      } satisfies ErrorResponse;
    }
    return response;
  }

  private toHubResponse<T>(response: GatewayAPIResponse<T>): ApiResponse<T> {
    if (response.success) {
      if (response.notModified === true) {
        return {
          success: true,
          data: (response.data ?? null) as T,
          message: response.message,
          meta: ApiClient.LEGACY_META,
          etag: response.etag,
          notModified: true,
        };
      }
      return {
        success: true,
        data: response.data as T,
        message: response.message,
        meta: ApiClient.LEGACY_META,
        etag: response.etag,
      };
    }
    if (response.errorDetail) {
      const gatewayError = response.errorDetail;
      const details = gatewayError.details && typeof gatewayError.details === "object" && !Array.isArray(gatewayError.details) ? { ...gatewayError.details } : {};
      const requestId = typeof details.request_id === "string" && details.request_id.trim() ? details.request_id.trim() : ApiClient.LEGACY_META.request_id;
      return {
        success: false,
        error: {
          code: gatewayError.code,
          message: gatewayError.message,
          details,
        },
        meta: {
          request_id: requestId,
          timestamp: new Date().toISOString(),
          version: "v1",
        },
      };
    }
    if (typeof response.error === "string") {
      const trimmed = response.error.trim();
      return {
        success: false,
        error: {
          code: "GATEWAY_ERROR",
          message: trimmed.length > 0 ? trimmed : "Request failed",
          details: {},
        },
        meta: ApiClient.LEGACY_META,
      };
    }
    return {
      success: false,
      error: {
        code: "GATEWAY_ERROR",
        message: "Request failed",
        details: {},
      },
      meta: ApiClient.LEGACY_META,
    };
  }

  /**
   * Retry request with exponential backoff.
   *
   * @param fn - Function to retry
   * @param retries - Number of retries remaining
   * @param delay - Delay in milliseconds
   * @returns Response or error
   */
  private async retry<T>(fn: () => Promise<T>, retries: number, delay: number): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = Number(error.response?.status || 0);
        const retryableStatus = status === 408 || status === 429 || status >= 500;
        const retryableNetworkCode = ["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK"].includes(String(error.code || ""));
        if (!retryableStatus && !retryableNetworkCode) {
          throw error;
        }
      }
      if (retries === 0) {
        throw error;
      }

      // Check if error is ErrorResponse type
      if (error && typeof error === "object" && "success" in error && error.success === false) {
        const errorObj = errorFromResponse(error as ErrorResponse);
        if (!isRetryableError(errorObj)) {
          throw error;
        }
      }

      await new Promise((resolve) => setTimeout(resolve, delay));
      return this.retry(fn, retries - 1, delay * 2);
    }
  }

  /**
   * GET request.
   *
   * @param url - Request URL
   * @param config - Axios config
   * @returns Response data
   */
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const headers = axios.AxiosHeaders.from(config?.headers).toJSON(true) as Record<string, string>;
    const response = await gatewayApiService.get<T>(this.gatewayPath(url), {
      headers,
      signal: config?.signal as AbortSignal | undefined,
    });
    return this.toHubResponse(response);
  }

  /**
   * POST request.
   *
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios config
   * @returns Response data
   */
  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const headers = axios.AxiosHeaders.from(config?.headers).toJSON(true);
    const timeoutMs = typeof config?.timeout === "number" ? config.timeout : undefined;
    const response = await gatewayApiService.post<T>(this.gatewayPath(url), data, {
      headers,
      signal: config?.signal as AbortSignal | undefined,
      ...(timeoutMs !== undefined ? { timeoutMs } : {}),
    });
    return this.toHubResponse(response);
    /*
    const isFormData = data instanceof FormData;
    return this.retry(
      async () => {
        // Handle FormData (file uploads) - don't set Content-Type, let browser set it with boundary
        const requestConfig = isFormData
          ? {
              ...config,
              headers: {
                ...config?.headers,
                "Content-Type": undefined, // Let browser set Content-Type with boundary
              },
            }
          : config;

        const response = await this.client.post<ApiResponse<T>>(url, data, requestConfig);
        return this.normalizeApiResponse<T>(response.data);
      },
      isFormData ? 0 : MAX_RETRIES,
      RETRY_DELAY
    );
    */
  }

  async postWithoutProjectContext<T>(url: string, data?: unknown): Promise<ApiResponse<T>> {
    const response = await gatewayApiService.postWithoutProjectContext<T>(this.gatewayPath(url), data);
    return this.toHubResponse(response);
  }

  /**
   * POST request for file download (returns Blob).
   *
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios config
   * @returns Blob data
   */
  async postFile(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<Blob> {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    const body = isFormData ? data : data !== undefined ? JSON.stringify(data) : undefined;
    const response = await this.fetchBinaryResponse(url, { method: "POST", body }, config);
    return response.blob();
  }

  /**
   * GET request for file download (returns Blob).
   *
   * @param url - Request URL
   * @param config - Axios config
   * @returns Blob data
   */
  async getFile(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.fetchBinaryResponse(url, { method: "GET" }, config);
    return response.blob();
  }

  /**
   * GET file download with Content-Disposition filename when present.
   */
  async getDownloadedFile(url: string, config?: AxiosRequestConfig): Promise<ApiDownloadedFile> {
    const response = await this.fetchBinaryResponse(url, { method: "GET" }, config);
    const blob = await response.blob();
    return {
      blob,
      filename: this.filenameFromDisposition(response.headers.get("Content-Disposition")),
    };
  }

  /**
   * PUT request.
   *
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios config
   * @returns Response data
   */
  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    void config;
    const response = await gatewayApiService.put<T>(this.gatewayPath(url), data);
    return this.toHubResponse(response);
    /*
    return this.retry(
      async () => {
        const response = await this.client.put<ApiResponse<T>>(url, data, config);
        return this.normalizeApiResponse<T>(response.data);
      },
      MAX_RETRIES,
      RETRY_DELAY
    );
    */
  }

  /**
   * PATCH request.
   *
   * @param url - Request URL
   * @param data - Request data
   * @param config - Axios config
   * @returns Response data
   */
  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    void config;
    const response = await gatewayApiService.patch<T>(this.gatewayPath(url), data);
    return this.toHubResponse(response);
    /*
    return this.retry(
      async () => {
        const response = await this.client.patch<ApiResponse<T>>(url, data, config);
        return this.normalizeApiResponse<T>(response.data);
      },
      MAX_RETRIES,
      RETRY_DELAY
    );
    */
  }

  /**
   * DELETE request.
   *
   * @param url - Request URL
   * @param config - Axios config
   * @returns Response data
   */
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const headers = axios.AxiosHeaders.from(config?.headers).toJSON(true);
    const payload = config?.data;
    let body: BodyInit | undefined;
    if (payload !== undefined && payload !== null) {
      if (typeof payload === "string" || payload instanceof FormData || payload instanceof Blob) {
        body = payload;
      } else {
        body = JSON.stringify(payload);
      }
    }
    const response = await gatewayApiService.delete<T>(this.gatewayPath(url), { headers, body });
    return this.toHubResponse(response);
    /*
    return this.retry(
      async () => {
        const response = await this.client.delete<ApiResponse<T>>(url, config);
        return this.normalizeApiResponse<T>(response.data);
      },
      MAX_RETRIES,
      RETRY_DELAY
    );
    */
  }

  /**
   * GET paginated request.
   *
   * @param url - Request URL
   * @param config - Axios config
   * @returns Paginated response data
   */
  async getPaginated<T>(url: string, config?: AxiosRequestConfig): Promise<PaginatedApiResponse<T>> {
    return this.get<unknown>(url, config) as Promise<PaginatedApiResponse<T>>;
  }

  /**
   * GET list request.
   *
   * @param url - Request URL
   * @param config - Axios config
   * @returns List response data
   */
  async getList<T>(url: string, config?: AxiosRequestConfig): Promise<ListApiResponse<T>> {
    return this.get<unknown>(url, config) as Promise<ListApiResponse<T>>;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
